import { ResourceInIncident } from '../../hooks/resourceInIncidentHooks';
import { ResourceSet } from '../../hooks/resourceSetHooks';
import { WorkAssignment } from '../../hooks/workAssignmentHooks';
import { ResourceSite } from '../../hooks/resourceSiteHooks';

interface ResourceWithSearchIndex extends ResourceInIncident {
  resource_name: string;
  resource_type: string;
  resource_kind?: string;
  resource_set_description?: string;
  resource_set_kind?: string;
  resource_set_type?: string;
  _searchableText: string;
}

/**
 * Creates a searchable index for resources by combining data from multiple
 * sources and generating a searchable text field
 *
 * @param resources - Array of resources in incident
 * @param resourceSets - Array of resource sets
 * @param workAssignments - Array of work assignments
 * @param resourceSites - Array of resource sites
 * @returns Array of resources with searchable index
 */
export const createResourcesWithSearchIndex = (
  resources: ResourceInIncident[],
  resourceSets: ResourceSet[],
  workAssignments: WorkAssignment[],
  resourceSites: ResourceSite[]
): ResourceWithSearchIndex[] => {
  return resources.map((resource) => {
    // Find the resource set for this resource
    const resourceSet = resourceSets.find(
      (rs) => rs.id === resource.resource_set_id
    );

    // Merge resource set data into resource
    const resourceWithSetData = {
      ...resource,
      // Use resource set data for type and name if available
      resource_name:
        resource.resource_name || resourceSet?.name || 'Unknown Resource',
      resource_type:
        resourceSet?.kind || resource.resource_kind || 'Unknown Kind',
      resource_kind: resourceSet?.kind || resource.resource_kind,
      resource_set_description: resourceSet?.description || '',
      resource_set_kind: resourceSet?.kind || '',
      resource_set_type: resourceSet?.type || '',
    };

    const searchableFields = [
      resourceWithSetData.resource_name,
      resourceWithSetData.resource_name_identifier,
      resourceWithSetData.resource_type,
      resourceWithSetData.resource_category,
      resourceWithSetData.resource_kind,
      (
        resourceWithSetData as ResourceInIncident & {
          resource_subtype?: string;
        }
      ).resource_subtype,
      resourceWithSetData.leader_name,
      resourceWithSetData.state_and_agency,
      resourceWithSetData.home_unit_agency,
      resourceWithSetData.order_request_number,
      resourceWithSetData.incident_contact_info,
      resourceWithSetData.incident_assignment_location,
      resourceWithSetData.departure_point,
      resourceWithSetData.checkout_destination,
      resourceWithSetData.checkout_contact_info,
      resourceWithSetData.checkout_remarks,
      resourceWithSetData.status_notes,
      resourceWithSetData.current_status,
      resourceWithSetData.release_method,
      resourceWithSetData.checkout_travel_method,
      resourceWithSetData.resource_personnel_released,
      resourceWithSetData.resource_set_description,
      resourceWithSetData.resource_set_kind,
      resourceWithSetData.resource_set_type,
    ];

    // Add resource set fields to searchable fields
    if (resourceSet) {
      searchableFields.push(resourceSet.name);
      searchableFields.push(resourceSet.description);
      searchableFields.push(resourceSet.kind);
      searchableFields.push(resourceSet.type);
    }

    // Add work assignment name if exists
    if (resourceWithSetData.work_assignment_id) {
      const workAssignment = workAssignments.find(
        (wa) => wa.id === resourceWithSetData.work_assignment_id
      );
      if (workAssignment) {
        searchableFields.push(workAssignment.work_assignment);
        searchableFields.push(workAssignment.branch);
        searchableFields.push(workAssignment.division);
      }
    }

    // Add resource site names if they exist
    [
      resourceWithSetData.assignment_site_id,
      resourceWithSetData.departure_site_id,
      resourceWithSetData.destination_site_id,
      resourceWithSetData.resource_site_id,
    ].forEach((siteId) => {
      if (siteId) {
        const site = resourceSites.find((s) => s.id === siteId);
        if (site) {
          searchableFields.push(site.name);
        }
      }
    });

    const searchableText = searchableFields
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return {
      ...resourceWithSetData,
      _searchableText: searchableText,
    };
  });
};

/**
 * Filters resources by search text
 *
 * @param resources - Array of resources with search index
 * @param searchText - Text to search for
 * @returns Filtered array of resources
 */
export const filterResourcesBySearchText = (
  resources: ResourceWithSearchIndex[],
  searchText: string
): ResourceWithSearchIndex[] => {
  if (!searchText) return resources;

  const lowerSearchText = searchText.toLowerCase();
  return resources.filter((resource) =>
    resource._searchableText.includes(lowerSearchText)
  );
};
